import { IExec } from "models/Exec.model";
import IOrg, { AdminIOrganization } from "models/Organization.model";
import IPermission from "models/Permission.model";
import { IRepo } from "models/Repo.model";
import IRole, { type OrgRoleIds } from "models/Role.model";
import ISecret, {
  DestConfig,
  DestType,
  HierarchyType,
  SrcConfig,
  SrcType,
  CreateSecretReq,
} from "models/Secret.model";
import {
  CreateCloudPolicyRequest,
  UpdateCloudPolicyDefaultRuleRequest,
  AddQuotasToCloudPolicyRequest,
  UpdateCloudQuotaRequest,
  ListCloudPolicyRequest,
  CloudPolicy,
  GetCloudQuotaUtilizationResponse,
  UpdateCloudQuotaResponse,
  UpdateCloudPolicyDefaultRuleResponse,
  UpdateCloudPolicySystemPolicyOverrideRequest,
  UpdateCloudPolicySystemPolicyOverrideResponse,
} from "components/CloudPolicy/types";
import IUser, { IdeConfig, ILoginToken, IUserMinimal } from "models/User.model";
import IWorkspace, { IFileObject } from "models/Workspace.model";
import IWorkspaceTemplate from "models/WorkspaceTemplate.model";
import { WorkspaceClassId } from "../models/Workspace.model";
import { api, AxiosRes, BaseParams, BaseRes } from "./config";
import { Applications } from "./applications";
import { DevplaneAdmin } from "./devplane/admin";
import { DevplaneLegacyAdapter } from "./devplaneLegacyAdapter";
import { DevplaneRemotefile } from "./devplane/remotefile";
import type { Organization as DevplaneOrganization } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_pb";
import type { Program } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/program_pb";
import type { GetDockerComposeResponse } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/remotefile_pb";
import type { ReservedInstancePool } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/reserved_instance_pool_pb";
import {
  CustomContainer,
  DockerCompose,
  LifeCycleScriptAttr,
} from "components/Environment/shared/BuildTypes";
import {
  GPUInstanceType,
  InstanceCloudCred,
} from "types/InstanceTypes";
import { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import {
  getAllInstanceTypesAvailableFromDevplane,
  getAllPublicInstanceTypesFromDevplane,
  getInstanceByTypeForCloudCredFromDevplane,
} from "./devplaneInstanceTypes";

interface UserLoginReqBody {
  username: string;
  password: string;
}

interface UserRefreshTokenReqBody {
  refresh_token: string;
}

type UsersMinimalRes = {
  data?: IUserMinimal[];
} & AxiosRes &
  BaseRes;
type UserMinimalRes = {
  data?: IUserMinimal;
} & AxiosRes &
  BaseRes;
export type CreateUserRes = {
  data?: {
    user: IUser;
    errorMessage: string;
  };
} & AxiosRes &
  BaseRes;

export type UserRes = {
  data?: IUser;
} & AxiosRes &
  BaseRes;

type MultipleUserRes = {
  data?: IUser[];
} & AxiosRes &
  BaseRes;

type LoginRes = {
  data?: ILoginToken;
} & AxiosRes &
  BaseRes;

type QueryByUsernameParams = {
  username: string;
} & BaseParams;

type QueryByAuthIDParams = {
  authID: string;
} & BaseParams;

export interface UserUpdateReqBody {
  username: string;
  name: string;
  email: string;
  baseWorkspaceRepo?: string;
  userSetupExecPath?: string;
  onboardingData?: {
    editor?: string;
    SSH?: boolean;
    usedCli?: boolean;
    copiedCliFromConsole?: boolean;
  };
  apiKeys?: {
    huggingFace: string;
    ngc: string;
  };
}

type PublicUserResponse = {
  username: string;
};

type PublicUserRes = {
  data?: PublicUserResponse;
} & AxiosRes &
  BaseRes;

const Users = {
  registerUser: (rawToken: string, inviteToken: string | null) => {
    const config = { headers: { Identity: rawToken } };
    return api.post(
      "/api/users",
      { inviteToken },
      config,
    ) as Promise<CreateUserRes>;
  },
  getAllAsAdmin: () =>
    api.get("/api/users?all=true") as Promise<MultipleUserRes>,
  queryByUsername: (username: string) => {
    const params: QueryByUsernameParams = { username };
    return api.get("/api/users", { params }) as Promise<UsersMinimalRes>;
  },
  queryByAuthID: (authID: string) => {
    const params: QueryByAuthIDParams = { authID };
    return api.get("/api/users", { params }) as Promise<UserMinimalRes>;
  },
  getById: (id: string) => api.get(`/api/users/${id}`) as Promise<UserRes>,
  update: (id: string, updates: UserUpdateReqBody) =>
    api.put(`/api/users/${id}`, updates) as Promise<UserRes>,
  login: (username: string, password: string) => {
    const args: UserLoginReqBody = { username, password };
    return api.post("/api/token/login", args) as Promise<LoginRes>;
  },
  refresh: (refreshToken: string) => {
    const args: UserRefreshTokenReqBody = { refresh_token: refreshToken };
    return api.post("/api/token/refresh", args) as Promise<LoginRes>;
  },
  getPublicUser: (userId: string) => {
    return api.get(`/api/user/${userId}`) as Promise<PublicUserRes>;
  },
};

type WorkspaceTemplateRes = {
  data?: IWorkspaceTemplate[];
} & AxiosRes &
  BaseRes;

const WorkspaceTemplate = {
  getAll: () =>
    api.get("/api/workspace_templates") as Promise<WorkspaceTemplateRes>,
};

type RolesRes = {
  data?: IRole[];
} & AxiosRes &
  BaseRes;

type RoleRes = {
  data?: IRole;
} & AxiosRes &
  BaseRes;

const Roles = {
  getAll: () => api.get("/api/roles") as Promise<RolesRes>,
  get: (id: string) => api.get(`/api/roles/${id}`) as Promise<RoleRes>,
};

type BillingProfileDetails = {
  account_email?: string;
  accoun_name?: string;
};

type CreditDetails = {
  top_up_threshold?: number; // in cents
  top_up_amount?: number; // in cents
  out_of_credits_threshold?: number; // in cents
  remaining_credits?: number; // in cents
};

export type BillingProfile = {
  billing_profile_id: string;
  create_time?: Date;
  update_time?: Date;
  labels: { [key: string]: string };
  billing_profile_meta?: BillingProfileDetails;
  external_account_id?: string;
  usage_labels: { [key: string]: string };
  usage_cloud_cred_labels: { [key: string]: string };
  collection_status?: string;
  inactive_at?: Date; // this is for when they have paused it or not
  delinquent_at?: Date;
  payment_status?: string;
  cloud_point_details?: {
    cloud_points_redeemable: number;
  };
  // BANANA
  creditBalance?: number;
  // BANANA
  billingThreshold?: number;
  // BANANA
  planIds: string[];
  credit_details?: CreditDetails;
  billing_type?: "credit" | "usage-arrears";
  resource_limits?: {
    max_resources?: number;
  };
};

export type Invoice = {
  id?: string;
  next_payment_attempt?: number;
  total?: number;
  tax?: number;
  subtotal?: number;
  customer_id?: string;
  status: string; // paid, void, draft, uncollectible, open
  currency?: string;
  amount_paid?: number;
  amount_due?: number;
  attempt_count?: number;
  invoice_url?: string;
  created?: number;
  invoice_id?: string;
  refund?: {
    refund_id: string;
    refunded_at: number;
    refund_url?: string;
    refund_status: string;
  };
};

type InvoiceRes = {
  data?: Invoice | undefined;
} & AxiosRes &
  BaseRes;

type ListInvoicesParams = {
  pageSize?: number;
  pageToken?: string;
};

export type ListInvoicesResponse = {
  invoices?: Invoice[];
  next_page_token?: string;
};

type ListInvoicesRes = {
  data?: ListInvoicesResponse | undefined;
} & AxiosRes &
  BaseRes;

type BillingProfileRes = {
  data?: BillingProfile | undefined;
} & AxiosRes &
  BaseRes;

type UpdateBillingProfileRes = {
  data?: {
    billing_profile: BillingProfile;
  };
} & AxiosRes &
  BaseRes;

type SubscriptionRes = {
  data?: undefined;
} & AxiosRes &
  BaseRes;

interface RetryInvoiceRequest {
  invoiceId: string;
}

interface CreateTemplateRequest {
  isPublic: boolean;
  workspaceRequest: CreateWorkspaceReqBody;
}

type RedemptionRes = {
  data?: {
    transaction: {
      amount_usd: string;
    };
  };
} & AxiosRes &
  BaseRes;

type CreditsRes = {
  data?: {
    balance_usd: number;
  };
} & AxiosRes &
  BaseRes;

// type OrgUsage struct {
// 	envID   string
// 	envName string
// 	cost    string
// }

type UsageRes = {
  data?: {
    Usage: UsageResp[];
  };
} & AxiosRes &
  BaseRes;

export type UsageResp = {
  EnvID: string;
  EnvName: string;
  CreatedByUserID: string;
  Cost: string;
  InstancePrice: string;
  UsageType: string;
  TotalTime: number; // seconds
};

type UsageOverviewRes = {
  data?: {
    Usage: UsageOverview[];
  };
} & AxiosRes &
  BaseRes;

export type UsageOverview = {
  Date: string;
  ComputeCost: number;
  StorageCost: number;
  InferenceCost: number;
  TotalTime: number;
};

export type UsageTableRow = {
  EnvID: string;
  EnvName: string;
  CreatedByUserID: string;
  ComputeCost: number;
  StorageCost: number;
  TotalCost: number;
  TotalTime: number; // seconds
};

interface ValidateInviteArgs {
  token: string;
}
export type NGCEnablement = 2;

type CreateNGCEnablementReqBody = {
  enablements: NGCEnablement[];
};

export type OrgRes = {
  data?: IOrg;
} & AxiosRes &
  BaseRes;

type CreateNGCEnablementRes = {
  data?: unknown;
} & AxiosRes &
  BaseRes;

type InviteLinkValidationRes = {
  data?: {
    valid: boolean;
  };
} & AxiosRes &
  BaseRes;

export type InviteLinkOrganizationAccessLevel =
  | OrgRoleIds.Admin
  | OrgRoleIds.Maintainer
  | OrgRoleIds.Member;

const Organizations = {
  createInviteLink: (
    id: string,
    organizationAccessLevel?: InviteLinkOrganizationAccessLevel,
  ) =>
    api.get(`/api/organizations/${id}/invite`, {
      params: { organizationAccessLevel },
    }) as Promise<any>,
  validateInvite: (token: string) => {
    const args: ValidateInviteArgs = { token };
    return api.post(
      `/api/invite/verify`,
      args,
    ) as Promise<InviteLinkValidationRes>;
  },
  connectCloud: (id: string) =>
    api.get(`/api/organizations/${id}/cloudformation_url`) as Promise<any>,
  inviteByEmail: (id: string, inviteEmails: string[]) =>
    api.post(`/api/organizations/${id}/email_invite`, {
      inviteEmails,
    }) as Promise<any>,
  getUsage: (id: string, dateRange?: { from: Date; to: Date }) => {
    let url = `/api/organizations/${id}/usage`;
    if (dateRange) {
      const params = new URLSearchParams({
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
      });
      url += `?${params.toString()}`;
    }
    return api.get(url) as Promise<UsageRes>;
  },
  getUsageOverview: (id: string, dateRange?: { from: Date; to: Date }) => {
    let url = `/api/organizations/${id}/usage-overview`;
    if (dateRange) {
      const params = new URLSearchParams({
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
      });
      url += `?${params.toString()}`;
    }
    return api.get(url) as Promise<UsageOverviewRes>;
  },
  getBillingProfile: (id: string) =>
    api.get(
      `/api/organizations/${id}/billingprofile`,
    ) as Promise<BillingProfileRes>,
  getCredits: (id: string) =>
    api.get(`/api/organizations/${id}/credits`) as Promise<CreditsRes>,
  getInvoices: (id: string, params?: ListInvoicesParams) => {
    const query = new URLSearchParams();
    if (params?.pageSize) {
      query.set("pageSize", `${params.pageSize}`);
    }
    if (params?.pageToken) {
      query.set("pageToken", params.pageToken);
    }

    const queryString = query.toString();
    return api.get(
      `/api/organizations/${id}/invoices${queryString ? `?${queryString}` : ""}`,
    ) as Promise<ListInvoicesRes>;
  },
  redeemCode: (id: string, code: string) =>
    api.post(`/api/organizations/${id}/credits/code/redeem`, {
      Code: code,
    }) as Promise<RedemptionRes>,
  createTemplate: (orgId: string, args: CreateTemplateRequest) =>
    api.post(`/api/organizations/${orgId}/template`, args) as Promise<any>,
  createNGCEnablements: (orgId: string, enablements: NGCEnablement[]) => {
    const args: CreateNGCEnablementReqBody = { enablements };
    return api.post(
      `/api/organizations/${orgId}/ngc_enablements`,
      args,
    ) as Promise<CreateNGCEnablementRes>;
  },
};

//*******************************************
//************ BILLING CREDITS **************
//*******************************************
type CreditsResponse = {
  totalCredits: number;
} & AxiosRes;

type AddCreditsRequest = {
  amount: number; // Amount of credits to add
};
type AutoRechargeSettings = {
  threshold: number; // When the balance goes below this amount
  rechargeAmount: number; // Amount to recharge when balance goes below the threshold
};
type NotificationThreshold = {
  threshold: number; // When the balance goes below this amount, a notification is triggered
};

type AddCreditsResponse = {
  status: "Success" | "Failed";
  message?: string;
} & AxiosRes;

type AutoRechargeSettingsResponse = AutoRechargeSettings & AxiosRes;

type NotificationThresholdResponse = NotificationThreshold & AxiosRes;

type UpdateBillingProfileRequest = {
  top_up_amount?: string;
  top_up_threshold?: string;
};

type UpdateBillingProfileResponse = {
  status: "Success" | "Failed";
  message?: string;
} & AxiosRes;

type PurchaseCreditsRequest = {
  amount: string;
};

// TODO: SET THIS TO FALSE WHEN APIs ARE READY
const MOCK = false;

function mockResponse<T>(data: T, delay = 1000): Promise<T & AxiosRes> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        ...data,
        status: 200,
        statusText: "OK",
        success: true,
        message: "Mocked response",
      });
    }, delay);
  });
}

const BillingCredits = {
  getCredits: (orgId: string) =>
    MOCK
      ? mockResponse({ totalCredits: 500 })
      : (api.get(
          `/api/organizations/${orgId}/credits`,
        ) as Promise<CreditsResponse>),

  getAutoRechargeSettings: (orgId: string) =>
    MOCK
      ? mockResponse({ threshold: 1000, rechargeAmount: 500 })
      : (api.get(
          `/api/organizations/${orgId}/credits/autorecharge`,
        ) as Promise<AutoRechargeSettingsResponse>),

  // Get billing profile
  getBillingProfile: (orgId: string) =>
    api.get(
      `/api/organizations/${orgId}/billingprofile`,
    ) as Promise<BillingProfileRes>,

  // Update billing profile
  updateBillingProfile: (orgId: string, args: UpdateBillingProfileRequest) =>
    MOCK
      ? mockResponse({
          status: "Billing Profile Updated",
          data: {
            billing_profile: {
              credit_details: { top_up_amount: 0, top_up_threshold: 0 },
            },
          },
        })
      : (api.put(
          `/api/organizations/${orgId}/billingprofile`,
          args,
        ) as Promise<UpdateBillingProfileRes>),

  // Purchase credits (adjusted for POST and the request structure)
  purchaseCredits: (orgId: string, args: PurchaseCreditsRequest) =>
    MOCK
      ? mockResponse({ status: "Credits Purchased" })
      : (api.post(
          `/api/organizations/${orgId}/purchaseCredits`,
          args,
        ) as Promise<AxiosRes>),

  // setNotificationThreshold: (orgId: string, threshold: NotificationThreshold) =>
  //   MOCK
  //     ? mockResponse({ threshold: 100 })
  //     : (api.post(
  //         `/api/organizations/${orgId}/credits/notificationthreshold`,
  //         threshold
  //       ) as Promise<NotificationThresholdResponse>),

  // getNotificationThreshold: (orgId: string) =>
  //   MOCK
  //     ? mockResponse({ threshold: 100 })
  //     : (api.get(
  //         `/api/organizations/${orgId}/credits/notificationthreshold`
  //       ) as Promise<NotificationThresholdResponse>),
};

interface MetricBodyReq {
  type: string;
}

export type PermissionsRes = {
  data?: IPermission[];
} & AxiosRes &
  BaseRes;

export const ALL_TIMEFRAMES = ["all", "year", "week"] as const;
type TimeframeTuple = typeof ALL_TIMEFRAMES;
export type Timeframe = TimeframeTuple[number];

export type EnvEventArgs = {
  frame: Timeframe;
};

type ModifyVolumeArgs = {
  diskSize: string;
};

type ShareWorkspaceArgs = {
  addUsers: string[];
  organizationID: string;
};

export interface NGCAPIKeyPolicyResource {
  id?: string;
  type?: string;
}

export interface NGCAPIKeyPolicy {
  product: string;
  scopes?: string[];
  resources?: NGCAPIKeyPolicyResource[];
}

export interface NGCAPIKeySpec {
  type: string;
  policies: NGCAPIKeyPolicy[];
}

export interface EnvEvent {
  Action: number;
  Email: string;
  Price: string;
  EnvName: string;
  EnvironmentId: string;
  Date: string;
  FormattedDate: string;
}

export interface EnvSaving {
  timeSaved: number;
  moneySaved: number;
}
type EnvSavingsRes = {
  data?: EnvSaving;
} & AxiosRes &
  BaseRes;

export type IExecName = string;

export interface CreateWorkspaceReqBody {
  reposV1?: { [key: string]: IRepo };
  execsV1?: { [key: string]: IExec };
  name: string;
  description?: string;
  workspaceTemplateId: string;
  cloudCredId?: string;
  workspaceGroupId?: string;
  workspaceClassId?: WorkspaceClassId;
  version?: string;
  isStoppable?: boolean;
  networkId?: string;
  ideConfig?: IdeConfig;
  initBranch?: string;
  dotBrevPath?: string;
  instanceType?: string;
  location?: string;
  subLocation?: string;
  diskStorage?: string;
  onContainer?: boolean;
  baseImage?: string;
  customContainer?: CustomContainer;
  vmBuild?: VMBuild;
  portMappings?: Record<string, string> | null;
  files?: LaunchableFileRequest[] | null;
  labels?: Record<string, string> | null;
  verbYaml?: string;
  workspaceVersion?: "v1" | "v0";
  dockerCompose?: DockerCompose;
  launchableConfig?: LaunchableConfig;
  exposedPorts?: string[];
  firewallRules?: CreateFirewallRule[];
  ngcApiKeySpec?: NGCAPIKeySpec;
}

export interface UpdateWorkspaceReqBody {
  workspaceClassId?: string;
  instanceType?: string;
  isStoppable?: boolean;
  startupScriptPath?: string;
  name?: string;
  ideConfig?: IdeConfig;
  reposV1?: { [key: string]: IRepo };
  execsV1?: { [key: string]: IExec };
  addPort?: CreateFirewallRule;
  revokePort?: string;
}
export interface AddFileAndBuildVerbContainerReqBody {
  fileObject: IFileObject;
  verbYaml: string;
  idempotencyKey?: string;
}

export interface ValidateDockerComposeReqBody {
  fileUrl?: string;
  yamlString?: string;
}

interface ReadFileReqBody {
  filePath: string;
}

export interface CloneWorkspaceReqBody {
  name: string;
}

export type WorkspaceRes = {
  data?: IWorkspace;
} & AxiosRes &
  BaseRes;

export type WorkspacesRes = {
  data?: IWorkspace[];
} & AxiosRes &
  BaseRes;

export type BuildVerbRes = {
  data?: VerbBuild;
} & AxiosRes &
  BaseRes;

interface VerbBuild {
  logFilePath: string;
}

export type ReadFileRes = {
  data?: ReadFileContent;
} & AxiosRes &
  BaseRes;

interface ReadFileContent {
  content: string;
}

type FileObjectRes = {
  data?: IFileObject[];
} & AxiosRes &
  BaseRes;

type IFileObjectMap = { [filePath: string]: IFileObject };

type FileMapRes = {
  data?: IFileObjectMap;
} & AxiosRes &
  BaseRes;

type LifeCycleScriptRes = {
  data?: LifeCycleScriptResponse;
} & AxiosRes &
  BaseRes;

interface LifeCycleScriptResponse {
  lifecycle_script: string;
  attrs: LifeCycleScriptAttr;
}

const Workspaces = {
  getFiles: (wsId: string) =>
    api.get(`/api/workspaces/${wsId}/files`) as Promise<FileObjectRes>,

  addFile: (wsId: string, file: IFileObject) =>
    api.post(`/api/workspaces/${wsId}/files`, {
      fileObject: file,
    }) as Promise<FileMapRes>,

  deleteFile: (wsId: string, filePath: string) =>
    api.delete(
      `/api/workspaces/${wsId}/files?path=${filePath}`,
    ) as Promise<void>,

  getAllAsAdmin: () =>
    api.get(`/api/workspaces/admin`) as Promise<WorkspacesRes>,
  getAll: (orgId: string) =>
    api.get(`/api/organizations/${orgId}/workspaces`) as Promise<WorkspacesRes>,
  get: (wsId: string) =>
    api.get(`/api/workspaces/${wsId}`) as Promise<WorkspaceRes>,
  create: (orgId: string, args: CreateWorkspaceReqBody) =>
    api.post(
      `/api/organizations/${orgId}/workspaces`,
      args,
    ) as Promise<WorkspaceRes>,
  update: (wsId: string, args: UpdateWorkspaceReqBody) =>
    api.put(`/api/workspaces/${wsId}`, args) as Promise<WorkspaceRes>,
  addFileAndBuildVerb: (
    wsId: string,
    args: AddFileAndBuildVerbContainerReqBody,
  ) =>
    api.post(
      `/api/workspaces/${wsId}/addfiles_and_buildverb`,
      args,
    ) as Promise<BuildVerbRes>,

  validateDockerCompose: (args: ValidateDockerComposeReqBody) =>
    DevplaneRemotefile.validateDockerCompose(
      args,
    ) as Promise<GetDockerComposeResponse>,

  readFileFromWorkspace: (wsId: string, args: ReadFileReqBody) =>
    api.post(`/api/workspaces/${wsId}/readfile`, args) as Promise<ReadFileRes>,
  stop: (wsId: string) =>
    api.put(`/api/workspaces/${wsId}/stop`) as Promise<WorkspaceRes>,
  start: (wsId: string) =>
    api.put(`/api/workspaces/${wsId}/start`) as Promise<WorkspaceRes>,
  reset: (wsId: string) =>
    api.put(`/api/workspaces/${wsId}/reset`) as Promise<WorkspaceRes>,
  hardReset: (wsId: string) =>
    api.put(`/api/workspaces/${wsId}/hardreset`) as Promise<WorkspaceRes>,
  clone: (wsId: string, args: CloneWorkspaceReqBody) =>
    api.post(`/api/workspaces/${wsId}/clone`, args) as Promise<WorkspaceRes>,
  fork: (wsId: string, args: CloneWorkspaceReqBody) =>
    api.post(`/api/workspaces/${wsId}/fork`, args) as Promise<WorkspaceRes>,
  envSavings: (wsId: string, args: EnvEventArgs) => {
    // add query params to url
    let url = `/api/workspaces/${wsId}/env_savings`;
    url = addQueryParams(url, args);

    return api.get(url) as Promise<EnvSavingsRes>;
  },
  modifyVolume: (wsId: string, args: ModifyVolumeArgs) =>
    api.post(
      `/api/workspaces/${wsId}/modify_volume`,
      args,
    ) as Promise<WorkspaceRes>,
  shareWorkspace: (wsId: string, args: ShareWorkspaceArgs) =>
    api.post(
      `/api/workspaces/${wsId}/share_workspace`,
      args,
    ) as Promise<WorkspaceRes>,
  getLifeCycleScript: (envId: string, lifecycleScriptId: string) =>
    api.get(
      `/api/launchable/lifecycle-script?envId=${envId}&scriptId=${lifecycleScriptId}`,
    ) as Promise<LifeCycleScriptRes>,
  getWorkspaceLifeCycleScript: (wsId: string, lifecycleScriptId: string) =>
    api.get(
      `/api/workspaces/${wsId}/lifecycle-script?scriptId=${lifecycleScriptId}`,
    ) as Promise<LifeCycleScriptRes>,
};

function addQueryParams(url: string, args: any) {
  const params = new URLSearchParams();
  Object.keys(args).forEach((key) => {
    params.append(key, args[key]);
  });
  return `${url}?${params.toString()}`;
}

const Instances = {
  get: (wsGroupId: string, region?: string) => {
    if (region) {
      return api.get(
        `api/instances/types/${wsGroupId}?region=${region}`,
      ) as Promise<any>;
    }
    return api.get(
      `api/instances/types/${wsGroupId}?region=us-west-2`,
    ) as Promise<any>;
  },
  getAllInstanceTypesAvailable: (
    orgId: string,
    imageID?: string,
    options?: {
      skipAccessFilter?: boolean;
      uniqueInstanceType?: boolean;
    },
  ) => {
    return getAllInstanceTypesAvailableFromDevplane(orgId, imageID, options);
  },
  getInstanceByType: (
    instanceType: string,
    cloudCredId: string,
    location?: string | null,
  ) => {
    return getInstanceByTypeForCloudCredFromDevplane(
      instanceType,
      cloudCredId,
      location,
    );
  },
  getAllPublicInstanceTypes: () => {
    return getAllPublicInstanceTypesFromDevplane();
  },
};

type AllInstanceTypeObjRes = {
  data?: AllInstanceTypeObj;
} & AxiosRes &
  BaseRes;

type CloudCredError = {
  cloudCred: InstanceCloudCred;
  error: string;
};

type AllInstanceTypeObj = {
  allInstanceTypes: GPUInstanceType[];
  cloudCredErrors?: CloudCredError[];
};

type GetSecretsParams = {
  hierarchyType: HierarchyType;
  hierarchyId: string;
} & BaseParams;

export type CreateSecretReqBody = CreateSecretReq;

export interface SecretReqSrc {
  type: SrcType;
  config: SrcConfig;
}

export interface SecretReqDest {
  type: DestType;
  config: DestConfig;
}
export interface UpdateSecretReqBody {
  id: string;
  name?: string;
  src?: SecretReqSrc;
  dest?: SecretReqDest;
}

type SecretsRes = {
  data?: { secret: ISecret; value: string }[];
} & AxiosRes &
  BaseRes;

type SecretRes = {
  data?: ISecret;
} & AxiosRes &
  BaseRes;

const Secrets = {
  get: (hierarchyType: HierarchyType, hierarchyId: string) => {
    const params: GetSecretsParams = { hierarchyType, hierarchyId };
    return api.get("/api/secrets", { params }) as Promise<SecretsRes>;
  },
  create: (args: CreateSecretReqBody) =>
    api.post("/api/secrets", args) as Promise<SecretRes>,
  update: (args: UpdateSecretReqBody) =>
    api.put("/api/secrets", args) as Promise<SecretRes>,
  delete: (secretId: string) =>
    api.delete(`/api/secrets/${secretId}`) as Promise<SecretRes>,
};

interface RenderRequest {
  sourceVal: SourceVal;
}

interface SourceVal {
  url: string;
  type: string;
}

const Notebooks = {
  preview: (request: RenderRequest) =>
    api.post("/api/notebooks/render", request),
};

type BillingClientCreditInfoRes = {
  data?: {
    last4?: string;
    brand?: string;
    expiration?: string;
  };
} & AxiosRes &
  BaseRes;

type BillingClientSecretRes = {
  data?: {
    clientSecret: string;
    last4?: string;
    brand?: string;
    expiration?: string;
  };
} & AxiosRes &
  BaseRes;
interface InvoiceReqBody {
  stripeId: string;
  descriptionMatcher: string;
  orgId: string;
}

type AdminOrgWithPM = {
  Org: AdminIOrganization;
  Email: string;
  TotalUsage: string;
  Workspaces: IWorkspace[];
};

type AdminOrgsWithPMRes = {
  data?: AdminOrgWithPM[];
} & AxiosRes &
  BaseRes;

type ResourceLims = {
  maxCostUtilizationPerHour: string;
  totalCostOfWorkspaces: string;
};

type ResourceLimitsRes = {
  data: ResourceLims[];
} & AxiosRes &
  BaseRes;

type PurchaseGiftCardReq = {
  amount: string;
  recipientName: string;
  recipientEmail: string;
  senderEmail: string;
  senderName: string;
  paymentMethod: string;
  message: string;
  description: string;
  hours: string;
};

type PurchaseGiftCardRes = {
  shortCode: string;
  giftCardId: string;
} & AxiosRes &
  BaseRes;

const Billing = {
  getPaymentMethods: (orgId: string) =>
    api.get(
      `/api/organizations/${orgId}/billing/paymentMethods`,
    ) as Promise<any>,
  deletePaymentMethod: (orgId: string, paymentMethodId: string) =>
    api.delete(
      `/api/organizations/${orgId}/billing/paymentMethods/${paymentMethodId}`,
    ) as Promise<any>,
  setDefaultPaymentMethod: (orgId: string, paymentMethodId: string) =>
    api.put(
      `/api/organizations/${orgId}/billing/paymentMethods/${paymentMethodId}/default`,
    ) as Promise<any>,
  getClientSecret: (orgID: string) =>
    api.get(
      `/api/organizations/${orgID}/billing/setup`,
    ) as Promise<BillingClientSecretRes>,
  getSavedCreditInfo: (orgID: string) =>
    api.get(
      `/api/organizations/${orgID}/billing`,
    ) as Promise<BillingClientCreditInfoRes>,
};

const Launchables = {
  listLaunchables: (orgId: string) =>
    api.get(
      `/api/organizations/${orgId}/launchables`,
    ) as Promise<ListLaunchableRes>,
  getPublicLaunchable: (launchableId: string) =>
    api.get(`/api/launchables/${launchableId}`) as Promise<GetLaunchableRes>,
  getLaunchableWithAuth: (launchableId: string) =>
    api.get(
      `/api/launchables/${launchableId}/now`,
    ) as Promise<GetLaunchableRes>,

  createLaunchable: (orgId: string, name: string, rawURL: string) =>
    api.post(`/api/organizations/${orgId}/launchables`, {
      name,
      rawURL,
    }) as Promise<CreateLaunchableRes>,
  createEnvironmentLaunchable: (
    orgId: string,
    createLaunchableRequest: CreateLaunchableRequest,
  ) =>
    api.post(
      `/api/organizations/${orgId}/v2/launchables`,
      createLaunchableRequest,
    ) as Promise<CreateLaunchableRes>,
  deleteLaunchable: (orgId: string, launchableId: string) =>
    api.delete(
      `/api/organizations/${orgId}/launchables/${launchableId}`,
    ) as Promise<AxiosRes & BaseRes>,

  getLaunchablePoints: (orgId: string) =>
    api.get(
      `/api/organizations/${orgId}/launchable-points`,
    ) as Promise<GetLaunchablePointsRes>,

  redeemLaunchablePoints: (orgId: string) =>
    api.post(
      `/api/organizations/${orgId}/redeem-cloud-points`,
      {},
    ) as Promise<GetLaunchablePointsRes>,

  editLaunchable: (orgId: string, envId: string, newRawURL: string) =>
    api.post(`/api/organizations/${orgId}/launchables/edit`, {
      envId,
      newRawURL,
    }) as Promise<EditLaunchableRes>,

  editEnvironmentLaunchable: (
    orgId: string,
    modifyEnvironmentRequest: ModifyLaunchableRequest,
  ) =>
    api.post(
      `/api/organizations/${orgId}/v2/launchables/edit`,
      modifyEnvironmentRequest,
    ) as Promise<EditLaunchableRes>,
  editLaunchableAccess: (
    orgId: string,
    editLaunchableAccessRequest: EditLaunchableAccessRequest,
  ) =>
    api.post(
      `/api/organizations/${orgId}/v2/launchables/access`,
      editLaunchableAccessRequest,
    ) as Promise<EditLaunchableAccessRes>,
};

type GetApiKeyRequest = {
  orgId: string;
};

type ApiKey = {
  name: string;
  value: string;
};

type GetApiKeyRes = {
  data: ApiKey[];
} & AxiosRes &
  BaseRes;

const InferenceEndpoints = {
  getApiKey: (args: GetApiKeyRequest) =>
    api.get(
      `/api/organizations/${args.orgId}/apikeys`,
    ) as Promise<GetApiKeyRes>,
};

type EditLaunchableRes = {
  data: string;
} & AxiosRes &
  BaseRes;

type EditLaunchableAccessRes = {
  data: {
    id: string;
  };
} & AxiosRes &
  BaseRes;

type LaunchableCloudPointsGenerated = {
  LaunchableCloudPointsGenerated: LaunchableCloudPoints[];
};

export type LaunchableCloudPoints = {
  LaunchableID: string;
  AmountGenerated: number;
};

type GetLaunchablePointsRes = {
  data: LaunchableCloudPointsGenerated;
} & AxiosRes &
  BaseRes;

type CreateLaunchableRes = {
  data: {
    id: string;
  };
} & AxiosRes &
  BaseRes;

export type LaunchableFromList = {
  id: string;
  name: string;
  rawURL?: string;
  createdAt?: string;
  updatedAt?: string;
  createdByUserId?: string;
  createdByOrgId?: string;
  createWorkspaceRequest?: LaunchableCreateWorkspaceRequest;
  buildRequest?: LaunchableBuildRequest;
  file?: LaunchableFileRequest;
  couponCode?: string;
  description?: string;
  viewAccess?: string;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
};

export type CreateLaunchableRequest = {
  name: string;
  description: string;
  viewAccess: "public" | "organization" | "published";
  createWorkspaceRequest: LaunchableCreateWorkspaceRequest;
  buildRequest: LaunchableBuildRequest;
  file?: LaunchableFileRequest | null;
};

export type ModifyLaunchableRequest = {
  id: string;
  name?: string;
  description: string;
  createWorkspaceRequest: LaunchableCreateWorkspaceRequest;
  buildRequest: LaunchableBuildRequest;
  file?: LaunchableFileRequest | null;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
};

type EditLaunchableAccessRequest = {
  id: string;
  viewAccess: string;
};

export type LaunchableFileRequest = {
  url: string;
  path: string;
};

export type LaunchableCreateWorkspaceRequest = {
  instanceType: string;
  cloudCredId?: string;
  cloud_cred_id?: string;
  workspaceGroupId?: string;
  storage?: string;
  location?: string;
  subLocation?: string;
  firewallRules?: CreateFirewallRule[];
  imageId?: string;
  performDefaultBuildForCustomImage?: boolean;
  ngcApiKeySpec?: NGCAPIKeySpec | null;
};

export interface LaunchableBuildRequest {
  verbBuild?: LaunchableVerbBuild;
  containerBuild?: CustomContainer;
  vmBuild?: VMBuild;
  dockerCompose?: DockerCompose;
  parameters?: LaunchableParameter[];
  ports: LaunchablePort[];
}

export interface LaunchableParameter {
  name: string;
  description?: string;
  required: boolean;
  text?: {
    defaultValue?: string;
  };
  choice?: {
    choices: string[];
    defaultValue?: string;
  };
}

export interface LaunchableParameterBinding {
  name: string;
  value?: string;
  brevSecretId?: string;
  managedSecret?: LaunchableManagedSecretReference;
}

export interface LaunchableManagedSecretReference {
  secretId: string;
  /** Omit the version ID to resolve the latest version at deployment time. */
  versionId?: string;
}

export interface LaunchableConfig {
  id: string;
  parameterBindings?: LaunchableParameterBinding[];
}

interface VMBuild {
  forceJupyterInstall: boolean;
  lifeCycleScriptAttr?: LifeCycleScriptAttr;
  k8s?: K8sConfig;
}

export interface K8sConfig {
  isDisabled: boolean;
  isDashboardEnabled: boolean;
}

export interface LaunchablePort {
  name: string;
  port: string;
  labels?: Record<string, string>;
}

interface LaunchableVerbBuild {
  verbYaml: string;
}

type ListLaunchableRes = {
  data: LaunchableFromList[];
} & AxiosRes &
  BaseRes;

type GetLaunchableRes = {
  data: LaunchableFromList;
} & AxiosRes &
  BaseRes;

// Cloud Policy Response Types
type CloudPoliciesRes = {
  data: CloudPolicy[];
  system_policies: CloudPolicy[];
  next_page_token?: string;
} & AxiosRes &
  BaseRes;

type CloudPolicyRes = {
  data: CloudPolicy;
} & AxiosRes &
  BaseRes;

type UpdateCloudPolicyDefaultRuleRes = {
  data: UpdateCloudPolicyDefaultRuleResponse;
} & AxiosRes &
  BaseRes;

type UpdateCloudPolicySystemPolicyOverrideRes = {
  data: UpdateCloudPolicySystemPolicyOverrideResponse;
} & AxiosRes &
  BaseRes;

type CloudPolicyUtilizationRes = {
  data: any;
} & AxiosRes &
  BaseRes;

type CloudQuotaUtilizationRes = {
  data: GetCloudQuotaUtilizationResponse;
} & AxiosRes &
  BaseRes;

type UpdateCloudQuotaRes = {
  data: UpdateCloudQuotaResponse;
} & AxiosRes &
  BaseRes;

type ListProgramsRes = {
  data: Program[];
} & AxiosRes &
  BaseRes;

type ProgramRes = {
  data: Program;
} & AxiosRes &
  BaseRes;

// Reserved Instance Pool Response Types
type ReservedInstancePoolsRes = {
  data: ReservedInstancePool[];
} & AxiosRes &
  BaseRes;

type ReservedInstancePoolRes = {
  data: ReservedInstancePool;
} & AxiosRes &
  BaseRes;

type UpdateOrgReservedInstancePoolsRes = {
  data: DevplaneOrganization;
} & AxiosRes &
  BaseRes;

// Cloud Credential Types
export interface CloudCredential {
  cloudCredId: string;
  providerId: string;
  name: string;
  organizationId?: string;
  isPrivate: boolean;
  labels?: { [key: string]: string };
  createdAt?: string;
}

export interface CreateCloudCredRequest {
  name: string;
  organizationId?: string;
  providerId: string;
  cloudCredId: string;
  isPrivate: boolean;
  labels?: { [key: string]: string };
  shadeform?: {
    apiKey: string;
    refId?: string;
  };
  lambdaLabs?: {
    apiKey: string;
  };
  crusoe?: {
    apiKey: string;
    secretKey: string;
    projectId: string;
  };
  aws?: {
    accessKeyId: string;
    secretAccessKey: string;
    roleArn?: string;
    externalId?: string;
  };
  azure?: {
    clientId: string;
    clientSecret: string;
    tenantId: string;
    subscriptionId: string;
    additionallyAllowedTenants?: string;
  };
  gcp?: {
    type?: string;
    projectId: string;
    privateKeyId?: string;
    privateKey: string;
    clientEmail: string;
    clientId?: string;
    authUri?: string;
    tokenUri?: string;
    authProviderX509CertUrl?: string;
    clientX509CertUrl?: string;
    reservationOnly?: boolean;
  };
  fluidstack?: {
    apiKey: string;
    apiToken: string;
  };
  launchpad?: {
    apiToken: string;
    provider?: string;
    apiUrl?: string;
  };
}

export interface CreateCloudCredResponse {
  cloudCred?: any;
  cloudCredId: string;
}

type CloudCredentialsRes = {
  data: CloudCredential[];
} & AxiosRes &
  BaseRes;

type CreateCloudCredRes = {
  data: CreateCloudCredResponse;
} & AxiosRes &
  BaseRes;

const Admin = {
  getOrgsWithPaymentMethods: () =>
    api.get(
      "/api/admins/orgs-with-payment-methods",
    ) as Promise<AdminOrgsWithPMRes>,
  sendInvoice: (args: InvoiceReqBody) =>
    api.post(`/api/admins/send-invoice`, args) as Promise<any>,

  blockUser: (userId: string) =>
    api.put(`/api/users/${userId}/block`) as Promise<UserRes>,

  unblockUser: (userId: string) =>
    api.put(`/api/users/${userId}/block`, {
      isBlocked: false,
    }) as Promise<UserRes>,
  setResourceLimits: (orgId: string, maxResources: number) =>
    api.post(`/api/admins/org/${orgId}/set-resource-limits`, {
      maxResources: maxResources, // in cents
    }) as Promise<AxiosRes & BaseRes>,
  addMemberToOrganization: (orgId: string, userId: string) =>
    api.post(`/api/admins/org/${orgId}/add-member-by-id`, {
      userId: userId,
    }) as Promise<AxiosRes & BaseRes>,
  // Cloud Policy routes
  listCloudPolicies: (orgId: string, request?: ListCloudPolicyRequest) =>
    DevplaneLegacyAdapter.listCloudPolicies(
      orgId,
      request,
    ) as Promise<CloudPoliciesRes>,

  createCloudPolicy: (orgId: string, request: CreateCloudPolicyRequest) =>
    DevplaneLegacyAdapter.createCloudPolicy(
      orgId,
      request,
    ) as Promise<CloudPolicyRes>,

  getCloudPolicy: (orgId: string, policyId: string) =>
    DevplaneLegacyAdapter.getCloudPolicy(
      orgId,
      policyId,
    ) as Promise<CloudPolicyRes>,

  deleteCloudPolicy: (orgId: string, policyId: string) =>
    DevplaneLegacyAdapter.deleteCloudPolicy(orgId, policyId) as Promise<
      AxiosRes & BaseRes
    >,

  updateCloudPolicyDefaultRule: (
    orgId: string,
    policyId: string,
    request: UpdateCloudPolicyDefaultRuleRequest,
  ) =>
    DevplaneLegacyAdapter.updateCloudPolicyDefaultRule(
      orgId,
      policyId,
      request,
    ) as Promise<UpdateCloudPolicyDefaultRuleRes>,

  updateCloudPolicySystemPolicyOverride: (
    orgId: string,
    policyId: string,
    request: UpdateCloudPolicySystemPolicyOverrideRequest,
  ) =>
    DevplaneLegacyAdapter.updateCloudPolicySystemPolicyOverride(
      orgId,
      policyId,
      request,
    ) as Promise<UpdateCloudPolicySystemPolicyOverrideRes>,

  getCloudPolicyUtilization: (orgId: string, policyId: string) =>
    DevplaneLegacyAdapter.getCloudPolicyUtilization(
      orgId,
      policyId,
    ) as Promise<CloudPolicyUtilizationRes>,

  addQuotasToCloudPolicy: (
    orgId: string,
    policyId: string,
    request: AddQuotasToCloudPolicyRequest,
  ) =>
    DevplaneLegacyAdapter.addQuotasToCloudPolicy(
      orgId,
      policyId,
      request,
    ) as Promise<CloudPolicyRes>,

  updateCloudQuota: (
    orgId: string,
    policyId: string,
    quotaId: string,
    request: UpdateCloudQuotaRequest,
  ) =>
    DevplaneLegacyAdapter.updateCloudQuota(
      orgId,
      policyId,
      quotaId,
      request,
    ) as Promise<UpdateCloudQuotaRes>,

  deleteCloudQuota: (orgId: string, policyId: string, quotaId: string) =>
    DevplaneLegacyAdapter.deleteCloudQuota(orgId, policyId, quotaId) as Promise<
      AxiosRes & BaseRes
    >,

  getCloudQuotaUtilization: (
    orgId: string,
    policyId: string,
    quotaId: string,
  ) =>
    DevplaneLegacyAdapter.getCloudQuotaUtilization(
      orgId,
      policyId,
      quotaId,
    ) as Promise<CloudQuotaUtilizationRes>,

  listPrograms: (_params?: URLSearchParams) =>
    DevplaneAdmin.listPrograms() as Promise<ListProgramsRes>,

  createProgram: (displayName: string) =>
    DevplaneAdmin.createProgram(displayName) as Promise<ProgramRes>,

  createCloudCredential: (request: CreateCloudCredRequest) =>
    api.put(
      `/api/admins/cloud-cred-workspace-group`,
      request,
    ) as Promise<CreateCloudCredRes>,

  // Reserved Instance Pool routes
  listReservedInstancePools: () =>
    DevplaneAdmin.listReservedInstancePools() as Promise<ReservedInstancePoolsRes>,

  updateOrgReservedInstancePools: (
    orgId: string,
    reservedInstancePoolIds: string[],
  ) =>
    DevplaneAdmin.updateOrgReservedInstancePools(
      orgId,
      reservedInstancePoolIds,
    ) as Promise<UpdateOrgReservedInstancePoolsRes>,

  createReservedInstancePool: (displayName: string) =>
    DevplaneAdmin.createReservedInstancePool(
      displayName,
    ) as Promise<ReservedInstancePoolRes>,

  updateReservedInstancePool: (poolId: string, displayName: string) =>
    DevplaneAdmin.updateReservedInstancePool(
      poolId,
      displayName,
    ) as Promise<ReservedInstancePoolRes>,

  deleteReservedInstancePool: (poolId: string) =>
    DevplaneAdmin.deleteReservedInstancePool(poolId) as Promise<
      AxiosRes & BaseRes
    >,

  deleteOrganization: (orgId: string) =>
    api.delete(`/api/admins/org/${orgId}`) as Promise<OrgRes>,
};

const ResourceLimits = {
  getResourceLimits: (orgId: string, userId: string) =>
    api.get(
      `/api/resourcelimits?orgId=${orgId || ""}&userId=${userId || ""}`,
    ) as Promise<ResourceLimitsRes>,
};


interface CreateInviteReqBody {
  emailAddress: string;
  expiresAt?: Date | null;
  message?: string;
  name?: string;
  organizationId: string;
  role: string;
}

interface Invite {
  id: string;
  emailAddress: string;
  expiresAt: Date | null;
  message: string;
  name: string;
  organizationId: string;
  role: string;
}

type InviteRes = {
  data: Invite;
} & AxiosRes &
  BaseRes;

const Invites = {
  accept: (inviteId: string) =>
    api.post(`/api/invites/${inviteId}/accept`) as Promise<InviteRes>,
  create: (request: CreateInviteReqBody) =>
    api.post(`/api/invites`, request) as Promise<InviteRes>,
};


export default {
  Users,
  WorkspaceTemplate,
  Roles,
  Workspaces,
  Organizations,
  Secrets,
  Billing,
  Instances,
  Admin,
  Applications,
  ResourceLimits,
  BillingCredits,
  Notebooks,
  Launchables,
  InferenceEndpoints,
  Invites,
};
